/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Switch } from 'antd'
import React from 'react'
import Text from '../Text'
import colors from '@sportninja/common/constants/appColors'

type SwitchInputProps = {
  /**
   * Widened from `string` so a label can carry an adjacent affordance (e.g. an
   * info tooltip). Purely additive — it already reached `Text` as children, so
   * every existing string caller is unaffected.
   */
  title: string | React.ReactNode
  subtitle?: string | React.ReactNode
  value: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
  titleColor?: string
  subtitleColor?: string
  /**
   * The subtitle's type size. A caption by default; a form whose subtitles are
   * whole sentences can set them at body size to keep them readable.
   */
  subtitleVariant?: 'paragraphXSmall' | 'paragraphSmall'
  switchColor?: string
  selectorColor?: string
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /**
   * Stable handle on the switch itself. The component renders no accessible
   * name (the title is a sibling `Text`, not a label), so without this the only
   * way to reach a specific switch is by DOM position — which silently retargets
   * whenever a conditionally-rendered switch is added or removed.
   */
  dataTestId?: string
}

export const SwitchInput: React.FC<SwitchInputProps> = ({
  title,
  subtitle,
  value,
  onChange,
  disabled,
  titleColor = colors.WHITE,
  subtitleColor = colors.NEUTRAL_50,
  subtitleVariant = 'paragraphXSmall',
  switchColor = colors.PRIMARY,
  selectorColor = colors.WHITE,
  tabIndex,
  dataTestId,
}) => {
  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          flex: 1 0 0;
          opacity: ${disabled ? 0.45 : 1};
        `}
      >
        <Text variant='paragraphSmall' weight='regular' color={titleColor}>
          {title}
        </Text>
        {subtitle ? (
          <Text
            variant={subtitleVariant}
            weight='regular'
            color={subtitleColor}
          >
            {subtitle}
          </Text>
        ) : null}
      </div>
      <Switch
        onChange={onChange}
        value={value}
        size='small'
        data-testid={dataTestId}
        css={css`
          align-self: center;
          &.ant-switch-checked {
            background-color: ${switchColor} !important;
          }
          &.ant-switch-checked:hover:not(.ant-switch-disabled) {
            background-color: ${switchColor} !important;
            opacity: 0.8;
          }
          &.ant-switch-checked:focus {
            background-color: ${switchColor} !important;
          }
          .ant-switch-handle::before {
            background-color: ${selectorColor} !important;
          }
          &.ant-switch-checked .ant-switch-handle::before {
            background-color: ${selectorColor} !important;
          }
          &.ant-switch-disabled {
            opacity: 0.25;
          }
        `}
        disabled={disabled}
        tabIndex={tabIndex}
      />
    </>
  )
}
